import { useState, useEffect } from "react";
import { updateProfile, verifyEmailChange } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { usePaymentHistory } from "../../hooks/usePaymentHistory";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Edit2, Lock } from "lucide-react";
import EditProfileModal from "../../components/Modal/EditProfileModal";
import VerifyEmailModal from "../../components/Modal/VerifyEmailModal";
import UpgradeModal from "../../components/Modal/UpgradeModal";
import { toast } from "react-hot-toast";
import { SUBSCRIPTION_PLANS } from "../../const/subscriptionPlan";

const Profile = () => {
	const { user, refreshUser } = useAuth();
	const navigate = useNavigate();
	const {
		paymentHistory,
		loading: loadingHistory,
		refetch: refetchPaymentHistory,
		page,
		totalPages,
		totalCount,
		pageSize,
		hasNext,
		hasPrev,
		goToPage,
	} = usePaymentHistory(user?._id || user?.id);

	// Fetch fresh account details and payment history each time Profile tab is opened
	useEffect(() => {
		if (user) {
			refreshUser().catch(() => {});
			refetchPaymentHistory();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const [updating, setUpdating] = useState(false);
	const requiresProfileCompletion = !user?.isProfileComplete;

	const handleProfileUpdate = async (data) => {
		setUpdating(true);
		try {
			const res = await updateProfile(data);
			if (res.emailVerificationRequired) {
				setIsEditModalOpen(false);
				setIsVerifyModalOpen(true);
				toast.success(res.message);
			} else {
				setIsEditModalOpen(false);
				toast.success("Profile updated successfully");
				await refreshUser(); // Refresh user data without full page reload
			}
		} catch (error) {
			console.log("Update failed", {error});
			toast.error(error.data?.message || "Failed to update profile");
		} finally {
			setUpdating(false);
		}
	};

	const handleEmailVerify = async (otp) => {
		try {
			await verifyEmailChange(otp);
			setIsVerifyModalOpen(false);
			toast.success("Email updated successfully");
			await refreshUser(); // Refresh user data without full page reload
		} catch (error) {
			throw new Error(error.response?.data?.message || "Verification failed");
		}
	};

	const handleEditClick = () => {
		// Always allow incomplete profiles to enter required birth details.
		if (requiresProfileCompletion || user?.planTier === SUBSCRIPTION_PLANS.PREMIUM) {
			setIsEditModalOpen(true);
		} else {
			setIsUpgradeModalOpen(true);
		}
	};

	const handleUpgrade = () => {
		// Redirect to plans page or handle API call
		navigate("/plans");
	};

	// PrivateRoute handles redirection, so we can just return null or loading state if user is missing temporarily
	if (!user) return null;

	return (
		<div
			className='min-h-screen bg-gray-900 text-white p-4 md:p-8'
			style={{
				backgroundImage: "url('/BGForSections.webp')",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<div className='max-w-6xl mx-auto'>
				<header className='mb-12'>
					<h1 className='text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2'>
						User Profile
					</h1>
					<p className='text-gray-400 text-lg'>
						Manage your account and view your order history.
					</p>
				</header>
				{requiresProfileCompletion && (
					<div className='mb-6 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-amber-200'>
						Please complete your birth details to continue using the app.
					</div>
				)}

				<div className='grid grid-cols-1 gap-8'>
					{/* User Info Card */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						className='rounded-2xl p-6 shadow-xl border border-purple-500/10 text-black'
						style={{
							background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
						}}
					>
						<div className="flex justify-between items-start mb-4">
							<h2 className='text-xl font-semibold text-black'>
								Account Details
							</h2>
							<button
								onClick={handleEditClick}
								className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors group"
								title={
									requiresProfileCompletion
										? "Complete Profile"
										: user?.planTier === SUBSCRIPTION_PLANS.PREMIUM
										? "Edit Profile"
										: "Upgrade to Edit"
								}
							>
								{requiresProfileCompletion || user?.planTier === SUBSCRIPTION_PLANS.PREMIUM ? (
									<>
										<Edit2 size={16} className="text-gray-400 group-hover:text-white" />
										<span className="text-sm font-medium text-gray-300 group-hover:text-white">
											{requiresProfileCompletion ? "Complete Profile" : "Edit"}
										</span>
									</>
								) : (
									<>
										<Lock size={16} className="text-amber-400" />
										<span className="text-sm font-medium text-amber-400">Unlock Editing</span>
									</>
								)}
							</button>
						</div>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm text-gray-500 mb-1'>Email</label>
								<div className='text-gray-900 font-medium'>{user.email}</div>
							</div>
							<div>
								<label className='block text-sm text-gray-500 mb-1'>Member Since</label>
								<div className='text-gray-900 font-medium'>
									{new Date(user?.createdAt)?.toLocaleDateString()}
								</div>
							</div>
							<div>
								<label className='block text-sm text-gray-500 mb-1'>Current Plan</label>
								<div className='text-gray-900 font-medium capitalize'>
									{user.planTier || "Free"}
								</div>
							</div>
						{user.planTier === 'premium' && (
								<>
									<div>
										<label className="block text-sm text-gray-500 mb-1">Credits Remaining</label>
										<div className="text-gray-900 font-medium">
											{user.credits || 0} <span className="text-xs text-gray-500">(1 credit = 1 Interpretation)</span>
										</div>
									</div>
									<div>
										<label className="block text-sm text-gray-500 mb-1">Subscription Expires</label>
										<div className="text-gray-900 font-medium">
											{user.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString() : '-'}
										</div>
									</div>
								</>
							)}
						</div>
						{user.planTier === 'premium' && (user.unlockedCharts?.length > 0) && (
							<div className="mt-6 border-t border-gray-200 pt-6">
								<h3 className="text-lg font-semibold mb-3">Unlocked Charts</h3>
								<ul className="space-y-2">
									{user.unlockedCharts.map((entry) => (
										<li key={entry.chartHash} className="text-sm text-gray-700 rounded-lg bg-gray-50 p-3">
											<div className="font-medium">
												{entry.birthData?.location?.name || 'Birth chart'}
											</div>
											<div className="text-gray-500">
												{entry.birthData?.date} {entry.birthData?.time}
												{entry.unlockedAt && (
													<span> · Unlocked {new Date(entry.unlockedAt).toLocaleDateString()}</span>
												)}
											</div>
										</li>
									))}
								</ul>
							</div>
						)}
					</motion.div>

					{/* Payment History Section */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.1 }}
						className='rounded-2xl p-6 shadow-xl border border-purple-500/10 text-black'
						style={{
							background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
						}}
					>
						<h2 className='text-xl font-semibold mb-6 text-black'>
							Payment History
						</h2>

						{loadingHistory ? (
							<div className='flex justify-center py-8'>
								<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500'></div>
							</div>
						) : paymentHistory.length > 0 ? (
							<>
								<div className='overflow-x-auto'>
									<table className='w-full text-left border-collapse'>
										<thead>
											<tr className='border-b border-gray-700 text-gray-400 text-sm uppercase tracking-wider'>
												<th className='py-4 px-4 font-medium'>Date</th>
												<th className='py-4 px-4 font-medium'>Report Type</th>
												<th className='py-4 px-4 font-medium'>Status</th>
												<th className='py-4 px-4 font-medium'>Action</th>
											</tr>
										</thead>
										<tbody className='text-black divide-y divide-gray-700/50'>
											{paymentHistory.map((order) => (
												<tr
													key={order._id}
													className='hover:bg-gray-700/30 transition-colors'
												>
													<td className='py-4 px-4 whitespace-nowrap'>
														{new Date(order.createdAt).toLocaleDateString("en-US", {
															year: "numeric",
															month: "long",
															day: "numeric",
														})}
													</td>
													<td className='py-4 px-4 capitalize font-medium text-black'>
														{order.planType?.replace(/_/g, " ") || "Report"}
													</td>
													<td className='py-4 px-4'>
														<span
															className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
																order.status === "completed"
																	? "bg-green-500/10 text-green-400 border border-green-500/20"
																	: order.status === "failed"
																	? "bg-red-500/10 text-red-400 border border-red-500/20"
																	: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
															}`}
														>
															{order.status}
														</span>
													</td>
													<td className='py-4 px-4'>
														{order.status === "completed" && order.reportUrl ? (
															<a
																href={order.reportUrl}
																target='_blank'
																rel='noopener noreferrer'
																className='inline-flex items-center text-black hover:text-gray-400 transition-colors text-sm font-medium'
															>
																<svg
																	className='w-4 h-4 mr-1.5'
																	fill='none'
																	stroke='currentColor'
																	viewBox='0 0 24 24'
																>
																	<path
																		strokeLinecap='round'
																		strokeLinejoin='round'
																		strokeWidth={2}
																		d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
																	/>
																</svg>
																Download PDF
															</a>
														) : (
															<span className='text-gray-600 text-sm'>-</span>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{totalPages > 1 && (
									<div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-700/50'>
										<span className='text-sm text-gray-500'>
											Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
										</span>
										<div className='flex items-center gap-2'>
											<button
												onClick={() => goToPage(page - 1)}
												disabled={!hasPrev}
												className='px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors'
											>
												Previous
											</button>
											<span className='px-3 py-1.5 text-sm text-gray-600'>
												Page {page} of {totalPages}
											</span>
											<button
												onClick={() => goToPage(page + 1)}
												disabled={!hasNext}
												className='px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors'
											>
												Next
											</button>
										</div>
									</div>
								)}
							</>
						) : (
							<div className='text-center py-12 text-gray-500 rounded-xl border border-dashed border-gray-700'>
								<p>No payment history found.</p>
							</div>
						)}
					</motion.div>
				</div>
			</div>

			<EditProfileModal
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				user={user}
				onUpdate={handleProfileUpdate}
				isUpdating={updating}
			/>

			<VerifyEmailModal
				isOpen={isVerifyModalOpen}
				onClose={() => setIsVerifyModalOpen(false)}
				onVerify={handleEmailVerify}
			/>

			<UpgradeModal
				isOpen={isUpgradeModalOpen}
				onClose={() => setIsUpgradeModalOpen(false)}
				onUpgrade={handleUpgrade}
			/>
		</div>
	);
};

export default Profile;
